import {
  Box,
  Button,
  ButtonProps,
  Card,
  Center,
  Group,
  Image,
  MantineColor,
  Space,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import _ from "lodash";
import { Link } from "react-router-dom";

import {
  AddonTemplate,
  AircraftAddonTemplate,
  isAircraftAddonTemplate,
  isBlankTemplate,
  isSoftwareAddonTemplate,
  SoftwareAddonTemplate,
} from "../../lib/model/addonTemplate";
import { isSerializedChecklist } from "../../lib/model/checklistSerialized";

export const AddonTemplateCard = ({
  addonTemplate,
  onPreview,
  primaryActionLabel,
  primaryAction,
  primaryActionButtonVariant,
  borderColor,
  linkToDetailView,
}: {
  addonTemplate: AddonTemplate;
  onPreview?: () => void;
  primaryActionLabel: string;
  primaryAction: () => void;
  primaryActionButtonVariant?: ButtonProps["variant"];
  borderColor?: MantineColor;
  linkToDetailView?: boolean;
}): JSX.Element => {
  const title = linkToDetailView ? (
    <Link
      to={`/templates/${addonTemplate.type}/${addonTemplate.id}`}
      style={{ color: "inherit", textDecoration: "inherit" }}
    >
      {addonTemplate.title}
    </Link>
  ) : (
    addonTemplate.title
  );

  return (
    <Card
      shadow="sm"
      p={0}
      radius="md"
      withBorder
      style={{
        height: "100%",
        borderColor: borderColor,
        borderWidth: borderColor ? "4px" : undefined,
      }}
    >
      <Stack justify="space-between" p={0} style={{ height: "100%" }} spacing={0}>
        <Card.Section>
          <Center>
            {addonTemplate.image ? (
              <Image src={addonTemplate.image} alt={addonTemplate.title} height={100} />
            ) : (
              <Space h={100} />
            )}
          </Center>
        </Card.Section>
        <Box px={"sm"}>
          <Title order={3} mt="xl">
            {title}
          </Title>
          <Text color="dimmed" size="sm">
            {addonContentsSummary(addonTemplate)}
          </Text>
        </Box>
        <Card.Section p="md">
          <Group position="apart" grow>
            <Button size="xs" onClick={primaryAction} variant={primaryActionButtonVariant}>
              {primaryActionLabel}
            </Button>
            {onPreview && (
              <Button size="xs" onClick={() => onPreview()} variant="light">
                Preview
              </Button>
            )}
          </Group>
        </Card.Section>
      </Stack>
    </Card>
  );
};

function addonContentsSummary(addon: AddonTemplate): string {
  if (isAircraftAddonTemplate(addon)) {
    return aircraftAddonContentsSummary(addon);
  } else if (isSoftwareAddonTemplate(addon)) {
    return softwareAddonContentsSummary(addon);
  } else if (isBlankTemplate(addon)) {
    return "";
  } else {
    const _exhaustiveCheck: never = addon;
    return _exhaustiveCheck;
  }
}

function aircraftAddonContentsSummary(addon: AircraftAddonTemplate): string {
  const checklistCount = addon.content.filter(isSerializedChecklist).length;
  const checklistItemCount = _.sum(
    addon.content.map((e) => {
      if (isSerializedChecklist(e)) {
        return e.content.length;
      } else {
        return 0;
      }
    })
  );
  return `${checklistItemCount} items across ${checklistCount} checklists`;
}
function softwareAddonContentsSummary(addon: SoftwareAddonTemplate): string {
  const checklistItemCount = _.sum(
    addon.content.map((e) => {
      return e.content.length;
    })
  );

  return `${checklistItemCount} checklist items`;
}
